import toastr from '@publicsafety/toastr';
import resourceHelper from '../inv-resource-helper/resourceHelper';
import resourceData from './resourceData';

/**
 * Module to handle logging of errors and warnings. The toastr library is used to display messages to user.
 */
function Logger() {

  var self = this;

  // Please ensure the supported language codes and default language code are all in lowercase
  var supportedLanguageCodes = ['en-us', 'cy', 'vi-vn'];
  var defaultLanguageCode = 'en-us';

  /** Flags if the debugMessage parameter on the error, warning and debug methods should be displayed to the user */
  var showErrorDebugMessage = true;

  /** Flags if calls to the logDebug should be handled or ignored */
  var processLogDebugCalls = false;

  toastr.options = {
    closeButton: true,
    progressBar: true,
    newestOnTop: true,
    timeOut: 4000
  };

  self.init = function (languageCode) {

    var languageCodeSupported;

    if (!languageCode) {
      self.logAndThrowUntranslatedError(
        'An issue occurred while initializing the logging module',
        'The required parameter \'languageCode\' was not provided when initializing the module \'inv-logging\'');
    }

    languageCode = languageCode.toLowerCase();
    languageCodeSupported = supportedLanguageCodes.indexOf(languageCode) > -1;
    languageCode = languageCodeSupported ? languageCode : defaultLanguageCode;
    resourceHelper.registerPackageResourceData(resourceData[languageCode], 'inv-logger', languageCode);
  };

  self.setOptions = function (options) {
    showErrorDebugMessage = options.showErrorDebugMessage;
    processLogDebugCalls = options.processLogDebugCalls;
  };

  /**
   * Displays an error to the user and logs to the console
   * @param {string} resourceKey
   * @param {string} [debugMessage] Message to be logged for debugging / internal purposes
   * @param {*} [errorObject]
   */
  self.error = function (resourceKey, debugMessage, errorObject) {

    var message = resourceHelper.getString(resourceKey);
    var errorDetails = createDetails(resourceKey, message, debugMessage, errorObject);

    if (showErrorDebugMessage && debugMessage) {
      message += ' ' + debugMessage;
    }

    console.error(errorDetails);
    toastr.error(message, null, { timeOut: 10000 });
  };

  /**
   * Displays an error to the user without a timeout (user has to dismiss it) and logs to the console
   * @param {string} resourceKey
   * @param {string} [debugMessage] Message to be logged for debugging / internal purposes
   * @param {*} [errorObject]
   */
  self.errorWithoutTimeout = function (resourceKey, debugMessage, errorObject) {

    var message = resourceHelper.getString(resourceKey);
    var errorDetails = createDetails(resourceKey, message, debugMessage, errorObject);

    if (showErrorDebugMessage && debugMessage) {
      message += ' ' + debugMessage;
    }

    console.error(errorDetails);
    toastr.error(message, null, { timeOut: null });
  };

  /**
   * Logs a warning
   * @param {string} resourceKey
   * @param {string} [debugMessage] Message to be logged for debugging / internal purposes
   * @param {Object} [errorObject]
   */
  self.warning = function (resourceKey, debugMessage, errorObject) {

    var message = resourceHelper.getString(resourceKey);
    var warningDetails = createDetails(resourceKey, message, debugMessage, errorObject);

    if (showErrorDebugMessage && debugMessage) {
      message += ' ' + debugMessage;
    }

    console.warn(warningDetails);
    toastr.warning(message, null, { timeOut: 10000 });
  };

  /**
   * Logs a debug message to the console for a technical / developer audience
   * @param {string} debugMessage
   * @param {*} [optionalParams]
   */
  self.debug = function (debugMessage, optionalParams) {

    if (processLogDebugCalls) {

      if (optionalParams) {
        console.debug(debugMessage, optionalParams);
      } else {
        console.debug(debugMessage);
      }
    }
  };

  self.success = function (resourceKey) {

    var message = resourceHelper.getString(resourceKey);
    toastr.success(message);
  };

  self.info = function (resourceKey) {

    var message = resourceHelper.getString(resourceKey);
    toastr.info(message);
  };

  /**
   * Logs an error to the console and displays an untranslated friendly message to the user. This method
   * must only be used for errors that occur during SPA startup when it's not possible to determine the
   * language for the user and therefore a translated friendly message can't be displayed.
   * @param {string} friendlyMessage
   * @param {string} [debugMessage]
   * @param {error} [errorObject]
   */
  self.logAndThrowUntranslatedError = function (friendlyMessage, debugMessage, errorObject) {

    errorObject = errorObject || {};

    // If the error has already been handled then nothing to do here
    if (errorObject.errorHasBeenLogged) {
      return;
    }

    var resourceKey = 'None: Untranslated error';
    var errorDetails = createDetails(resourceKey, friendlyMessage, debugMessage, errorObject);
    var message = friendlyMessage;

    if (showErrorDebugMessage && debugMessage) {
      message += ' ' + debugMessage;
    }

    console.error(errorDetails);
    toastr.error(message, null, { timeOut: null }); // Don't automatically hide the toastr, user must close.

    errorObject.errorHasBeenLogged = true;
    throw errorObject;
  };

  self.logUnauthorizedAccess = function () {

    var resourceKey = 'UnauthorizedAccessErrorMessage';
    var message = `You have attempted to access content or perform an action you aren’t currently authorized access to.
    This may be due to your session timing out. Please reload this page to re-authenticate and try again.`;

    if (resourceHelper.hasResourceData()) {
      message = resourceHelper.getString(resourceKey);
    }

    var warningDetails = createDetails(resourceKey, message);

    console.warn(warningDetails);
    toastr.warning(message, null, { timeOut: 10000, preventDuplicates: true });
  };

  self.logTimeout = function () {

    var resourceKey = 'AjaxTimeOutErrorMessage';
    var message = 'The server took too long to respond with data and a timeout occurred. Please try again later.';

    if (resourceHelper.hasResourceData()) {
      message = resourceHelper.getString(resourceKey);
    }

    var warningDetails = createDetails(resourceKey, message);

    console.warn(warningDetails);
    toastr.warning(message, null, { timeOut: 10000, preventDuplicates: true });
  };

  function createDetails(resourceKey, friendlyMessage, debugMessage, errorObject) {

    return {
      resourceKey: resourceKey,
      friendlyMessage: friendlyMessage,
      debugMessage: debugMessage,
      errorObject: errorObject
    };
  }
}

export default new Logger();
